import AddIcon from "@mui/icons-material/Add"
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined"
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined"
import PersonIcon from "@mui/icons-material/Person"
import RefreshIcon from "@mui/icons-material/Refresh"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import {
  activateEmployee,
  createEmployee,
  deactivateEmployee,
  getEmployeeFormData,
  getEmployees,
  updateEmployee,
} from "../../services/employeeService"


// =========================================================
// CONSTANTES
// =========================================================

const DEFAULT_EMPLOYMENT_TYPES = [
  {
    value: "FULL_TIME",
    label: "Temps plein",
  },
  {
    value: "PART_TIME",
    label: "Temps partiel",
  },
  {
    value: "CONTRACT",
    label: "Contractuel",
  },
  {
    value: "INTERN",
    label: "Stagiaire",
  },
  {
    value: "TEMPORARY",
    label: "Temporaire",
  },
]


const DEFAULT_STATUSES = [
  {
    value: "ACTIVE",
    label: "Actif",
  },
  {
    value: "INACTIVE",
    label: "Inactif",
  },
  {
    value: "SUSPENDED",
    label: "Suspendu",
  },
  {
    value: "TERMINATED",
    label: "Fin de contrat",
  },
]


const DEFAULT_GENDERS = [
  {
    value: "MALE",
    label: "Homme",
  },
  {
    value: "FEMALE",
    label: "Femme",
  },
]


const DEFAULT_CURRENCIES = [
  {
    value: "USD",
    label: "USD",
  },
  {
    value: "CDF",
    label: "CDF",
  },
]


// =========================================================
// HELPERS
// =========================================================

const getToday = () =>
  new Date()
    .toISOString()
    .slice(0, 10)


const createEmptyForm = () => ({
  first_name: "",
  last_name: "",
  phone: "",
  email: "",
  gender: "",
  birth_date: "",
  hire_date: getToday(),

  department_id: "",
  job_position_id: "",

  employment_type:
    "FULL_TIME",

  base_salary: 0,

  currency:
    "USD",

  work_location: "",

  status:
    "ACTIVE",

  notes: "",
})


const money = (
  value,
  currency = "USD"
) => {
  const amount =
    Number(value || 0)

  return `${new Intl.NumberFormat(
    "fr-FR",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }
  ).format(amount)} ${currency || "USD"}`
}


const normalizeArray = (
  value
) => {
  if (Array.isArray(value)) {
    return value
  }

  if (Array.isArray(value?.items)) {
    return value.items
  }

  if (Array.isArray(value?.data)) {
    return value.data
  }

  if (Array.isArray(value?.results)) {
    return value.results
  }

  return []
}


const normalizeOptions = (
  values,
  fallback = []
) => {
  const source =
    Array.isArray(values) &&
    values.length > 0
      ? values
      : fallback

  return source
    .map((item) => {
      if (
        typeof item ===
        "string"
      ) {
        return {
          value: item,
          label: item,
        }
      }

      if (
        item &&
        typeof item ===
          "object"
      ) {
        const value =
          item.value ??
          item.code ??
          item.name ??
          item.id

        const label =
          item.label ??
          item.name ??
          item.code ??
          item.value ??
          String(
            value ?? ""
          )

        return {
          ...item,
          value,
          label,
        }
      }

      return null
    })
    .filter(Boolean)
}


const extractErrorMessage = (
  error,
  fallback
) => {
  const detail =
    error?.response?.data
      ?.detail

  if (
    typeof detail ===
    "string"
  ) {
    return detail
  }

  if (
    detail &&
    typeof detail ===
      "object" &&
    !Array.isArray(detail)
  ) {
    return (
      detail.message ||
      detail.detail ||
      fallback
    )
  }

  if (
    Array.isArray(detail)
  ) {
    return detail
      .map(
        (item) =>
          item?.msg ||
          item?.message ||
          "Erreur de validation"
      )
      .join(" • ")
  }

  return (
    error?.response?.data
      ?.message ||
    error?.message ||
    fallback
  )
}


const getEmployeeFullName = (
  employee
) =>
  [
    employee?.first_name,
    employee?.last_name,
  ]
    .filter(Boolean)
    .join(" ")
    .trim()


const getDepartmentLabel = (
  department
) => {
  if (!department) {
    return "-"
  }

  const code =
    department.code || ""

  const name =
    department.name || ""

  if (code && name) {
    return `${code} - ${name}`
  }

  return (
    name ||
    code ||
    `Département #${department.id}`
  )
}


const getPositionLabel = (
  position
) => {
  if (!position) {
    return "-"
  }

  const code =
    position.code || ""

  const name =
    position.name || ""

  if (code && name) {
    return `${code} - ${name}`
  }

  return (
    name ||
    code ||
    `Poste #${position.id}`
  )
}


// =========================================================
// KPI CARD
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 3,
        minHeight: 120,
        border: "1px solid",
        borderColor: "divider",
        height: "100%",
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            fontWeight={900}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function EmployeesPage() {
  const [loading, setLoading] =
    useState(false)

  const [saving, setSaving] =
    useState(false)

  const [rows, setRows] =
    useState([])

  const [summary, setSummary] =
    useState({
      total: 0,
      active: 0,
      inactive: 0,
      monthly_base_payroll: 0,
    })

  const [
    formData,
    setFormData,
  ] = useState({
    statuses: [],
    employment_types: [],
    genders: [],
    currencies: [],
    departments: [],
    job_positions: [],
  })

  const [search, setSearch] =
    useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all")

  const [
    departmentFilter,
    setDepartmentFilter,
  ] = useState("all")

  const [open, setOpen] =
    useState(false)

  const [
    editingEmployee,
    setEditingEmployee,
  ] = useState(null)

  const [form, setForm] =
    useState(
      createEmptyForm()
    )

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  // =======================================================
  // OPTIONS
  // =======================================================

  const statusOptions =
    useMemo(
      () =>
        normalizeOptions(
          formData.statuses,
          DEFAULT_STATUSES
        ),
      [formData.statuses]
    )


  const employmentTypeOptions =
    useMemo(
      () =>
        normalizeOptions(
          formData.employment_types,
          DEFAULT_EMPLOYMENT_TYPES
        ),
      [
        formData.employment_types,
      ]
    )


  const genderOptions =
    useMemo(
      () =>
        normalizeOptions(
          formData.genders,
          DEFAULT_GENDERS
        ),
      [formData.genders]
    )


  const currencyOptions =
    useMemo(
      () =>
        normalizeOptions(
          formData.currencies,
          DEFAULT_CURRENCIES
        ),
      [formData.currencies]
    )


  const departmentOptions =
    useMemo(
      () =>
        normalizeArray(
          formData.departments
        ),
      [formData.departments]
    )


  const allPositionOptions =
    useMemo(
      () =>
        normalizeArray(
          formData.job_positions
        ),
      [formData.job_positions]
    )


  const positionOptions =
    useMemo(() => {
      if (!form.department_id) {
        return []
      }

      return allPositionOptions.filter(
        (position) =>
          String(
            position.department_id
          ) ===
          String(
            form.department_id
          )
      )
    }, [
      allPositionOptions,
      form.department_id,
    ])


  // =======================================================
  // NOTIFICATION
  // =======================================================

  const showMessage =
    useCallback(
      (
        message,
        severity = "success"
      ) => {
        setSnackbar({
          open: true,
          severity,
          message,
        })
      },
      []
    )


  // =======================================================
  // LOAD
  // =======================================================

  const loadEmployees =
    useCallback(
      async () => {
        setLoading(true)

        try {
          const result =
            await getEmployees({
              search:
                search.trim() ||
                undefined,

              status:
                statusFilter ===
                "all"
                  ? undefined
                  : statusFilter,

              department_id:
                departmentFilter ===
                "all"
                  ? undefined
                  : departmentFilter,
            })

          const items =
            Array.isArray(result)
              ? result
              : normalizeArray(
                  result
                )

          setRows(items)

          const backendSummary =
            !Array.isArray(result)
              ? result?.summary
              : null

          if (backendSummary) {
            setSummary({
              total:
                Number(
                  backendSummary.total ||
                    0
                ),

              active:
                Number(
                  backendSummary.active ||
                    0
                ),

              inactive:
                Number(
                  backendSummary.inactive ||
                    0
                ),

              monthly_base_payroll:
                Number(
                  backendSummary
                    .monthly_base_payroll ||
                    0
                ),
            })
          }
        } catch (error) {
          showMessage(
            extractErrorMessage(
              error,
              "Impossible de charger les employés."
            ),
            "error"
          )
        } finally {
          setLoading(false)
        }
      },
      [
        search,
        statusFilter,
        departmentFilter,
        showMessage,
      ]
    )


  const loadFormData =
    useCallback(
      async () => {
        try {
          const result =
            await getEmployeeFormData()

          setFormData({
            statuses:
              result?.statuses ||
              [],

            employment_types:
              result?.employment_types ||
              [],

            genders:
              result?.genders ||
              [],

            currencies:
              result?.currencies ||
              [],

            departments:
              result?.departments ||
              [],

            job_positions:
              result?.job_positions ||
              [],
          })
        } catch (error) {
          showMessage(
            extractErrorMessage(
              error,
              "Impossible de charger la structure RH."
            ),
            "error"
          )
        }
      },
      [showMessage]
    )


  useEffect(() => {
    loadFormData()
  }, [loadFormData])


  useEffect(() => {
    const timer =
      setTimeout(
        loadEmployees,
        300
      )

    return () =>
      clearTimeout(
        timer
      )
  }, [loadEmployees])


  // =======================================================
  // CREATE / EDIT
  // =======================================================

  const handleOpenCreate = () => {
    setEditingEmployee(null)

    setForm(
      createEmptyForm()
    )

    setOpen(true)
  }


  const handleOpenEdit = (
    employee
  ) => {
    setEditingEmployee(
      employee
    )

    setForm({
      first_name:
        employee.first_name ||
        "",

      last_name:
        employee.last_name ||
        "",

      phone:
        employee.phone ||
        "",

      email:
        employee.email ||
        "",

      gender:
        employee.gender ||
        "",

      birth_date:
        employee.birth_date ||
        "",

      hire_date:
        employee.hire_date ||
        getToday(),

      department_id:
        employee.department_id ||
        "",

      job_position_id:
        employee.job_position_id ||
        "",

      employment_type:
        employee.employment_type ||
        "FULL_TIME",

      base_salary:
        Number(
          employee.base_salary ||
            0
        ),

      currency:
        employee.currency ||
        "USD",

      work_location:
        employee.work_location ||
        "",

      status:
        employee.status ||
        "ACTIVE",

      notes:
        employee.notes ||
        "",
    })

    setOpen(true)
  }


  const handleCloseDialog =
    () => {
      if (saving) {
        return
      }

      setOpen(false)

      setEditingEmployee(
        null
      )
    }


  const handleChange =
    (field) =>
    (event) => {
      const value =
        event.target.value

      setForm(
        (current) => ({
          ...current,
          [field]: value,
        })
      )
    }


  const handleDepartmentChange =
    (event) => {
      const value =
        event.target.value

      setForm(
        (current) => ({
          ...current,

          department_id:
            value,

          // Important :
          // changement de département
          // => remise à zéro du poste.
          job_position_id:
            "",
        })
      )
    }


  const handleSave =
    async () => {
      const firstName =
        String(
          form.first_name ||
            ""
        ).trim()

      const lastName =
        String(
          form.last_name ||
            ""
        ).trim()

      if (
        !firstName ||
        !lastName ||
        !form.hire_date
      ) {
        showMessage(
          "Prénom, nom et date d'embauche sont obligatoires.",
          "warning"
        )

        return
      }

      if (
        !form.department_id
      ) {
        showMessage(
          "Veuillez sélectionner le département de l'employé.",
          "warning"
        )

        return
      }

      if (
        !form.job_position_id
      ) {
        showMessage(
          "Veuillez sélectionner le poste de l'employé.",
          "warning"
        )

        return
      }

      const salary =
        Number(
          form.base_salary ||
            0
        )

      if (
        !Number.isFinite(
          salary
        ) ||
        salary < 0
      ) {
        showMessage(
          "Le salaire de base doit être supérieur ou égal à zéro.",
          "warning"
        )

        return
      }

      const payload = {
        first_name:
          firstName,

        last_name:
          lastName,

        phone:
          String(
            form.phone ||
              ""
          ).trim() ||
          null,

        email:
          String(
            form.email ||
              ""
          ).trim() ||
          null,

        gender:
          form.gender ||
          null,

        birth_date:
          form.birth_date ||
          null,

        hire_date:
          form.hire_date,

        department_id:
          Number(
            form.department_id
          ),

        job_position_id:
          Number(
            form.job_position_id
          ),

        employment_type:
          form.employment_type,

        base_salary:
          salary,

        currency:
          form.currency,

        work_location:
          String(
            form.work_location ||
              ""
          ).trim() ||
          null,

        status:
          form.status,

        notes:
          String(
            form.notes ||
              ""
          ).trim() ||
          null,
      }

      setSaving(true)

      try {
        if (editingEmployee) {
          await updateEmployee(
            editingEmployee.id,
            payload
          )
        } else {
          await createEmployee(
            payload
          )
        }

        setOpen(false)

        setEditingEmployee(
          null
        )

        showMessage(
          editingEmployee
            ? "Employé modifié avec succès."
            : "Employé créé avec succès."
        )

        await loadEmployees()
      } catch (error) {
        showMessage(
          extractErrorMessage(
            error,
            "Impossible d'enregistrer l'employé."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // STATUS
  // =======================================================

  const handleToggleStatus =
    async (
      employee
    ) => {
      try {
        if (
          employee.status ===
          "ACTIVE"
        ) {
          await deactivateEmployee(
            employee.id
          )
        } else {
          await activateEmployee(
            employee.id
          )
        }

        showMessage(
          employee.status ===
            "ACTIVE"
            ? "Employé désactivé avec succès."
            : "Employé activé avec succès."
        )

        await loadEmployees()
      } catch (error) {
        showMessage(
          extractErrorMessage(
            error,
            "Impossible de modifier le statut."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field:
            "employee_code",

          headerName:
            "Matricule",

          minWidth: 125,

          renderCell:
            (params) => (
              <Typography
                variant="body2"
                fontWeight={800}
              >
                {params.value ||
                  "-"}
              </Typography>
            ),
        },

        {
          field:
            "full_name",

          headerName:
            "Employé",

          minWidth: 210,
          flex: 1.2,

          renderCell:
            (params) => (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                >
                  {getEmployeeFullName(
                    params.row
                  ) || "-"}
                </Typography>

                {params.row.email && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                  >
                    {params.row.email}
                  </Typography>
                )}
              </Box>
            ),
        },

        {
          field:
            "department",

          headerName:
            "Département",

          minWidth: 180,
          flex: 1,

          renderCell:
            (params) =>
              params.value ||
              "-",
        },

        {
          field:
            "job_title",

          headerName:
            "Poste",

          minWidth: 180,
          flex: 1,

          renderCell:
            (params) =>
              params.value ||
              "-",
        },

        {
          field:
            "phone",

          headerName:
            "Téléphone",

          minWidth: 140,

          renderCell:
            (params) =>
              params.value ||
              "-",
        },

        {
          field:
            "employment_type",

          headerName:
            "Type",

          minWidth: 130,

          renderCell:
            (params) => {
              const option =
                employmentTypeOptions.find(
                  (item) =>
                    String(
                      item.value
                    ) ===
                    String(
                      params.value
                    )
                )

              return (
                option?.label ||
                params.value ||
                "-"
              )
            },
        },

        {
          field:
            "base_salary",

          headerName:
            "Salaire base",

          minWidth: 150,

          align: "right",
          headerAlign: "right",

          renderCell:
            (params) =>
              money(
                params.row
                  .base_salary,

                params.row
                  .currency
              ),
        },

        {
          field:
            "status",

          headerName:
            "Statut",

          minWidth: 120,

          renderCell:
            (params) => {
              const status =
                String(
                  params.value ||
                    ""
                ).toUpperCase()

              const config = {
                ACTIVE: {
                  label: "Actif",
                  color: "success",
                },

                INACTIVE: {
                  label: "Inactif",
                  color: "default",
                },

                SUSPENDED: {
                  label: "Suspendu",
                  color: "warning",
                },

                TERMINATED: {
                  label:
                    "Fin contrat",
                  color: "error",
                },
              }

              const item =
                config[status] ||
                {
                  label:
                    params.value ||
                    "-",

                  color:
                    "default",
                }

              return (
                <Chip
                  size="small"
                  label={item.label}
                  color={item.color}
                />
              )
            },
        },

        {
          field:
            "actions",

          headerName:
            "Actions",

          minWidth: 220,

          sortable: false,
          filterable: false,

          renderCell:
            (params) => (
              <Stack
                direction="row"
                spacing={0.5}
              >
                <Button
                  size="small"
                  startIcon={
                    <EditOutlinedIcon />
                  }
                  onClick={() =>
                    handleOpenEdit(
                      params.row
                    )
                  }
                >
                  Modifier
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  color={
                    params.row
                      .status ===
                    "ACTIVE"
                      ? "error"
                      : "success"
                  }
                  onClick={() =>
                    handleToggleStatus(
                      params.row
                    )
                  }
                >
                  {params.row
                    .status ===
                  "ACTIVE"
                    ? "Désactiver"
                    : "Activer"}
                </Button>
              </Stack>
            ),
        },
      ],

      [
        employmentTypeOptions,
      ]
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },

          minWidth: 0,
        }}
      >
        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          spacing={2}
          mb={2}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={900}
            >
              Employés
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Gestion du personnel et rattachement Département → Poste → Employé.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                loading
                  ? (
                    <CircularProgress
                      size={18}
                    />
                  )
                  : (
                    <RefreshIcon />
                  )
              }
              onClick={
                loadEmployees
              }
              disabled={loading}
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={
                <AddIcon />
              }
              onClick={
                handleOpenCreate
              }
            >
              Nouvel employé
            </Button>
          </Stack>
        </Stack>


        <Alert
          severity="info"
          sx={{ mb: 2 }}
        >
          Chaque employé est maintenant rattaché à un département et à un poste
          existants. Le matricule est généré automatiquement par le système.
        </Alert>


        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",
              sm:
                "repeat(2, minmax(0, 1fr))",
              xl:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 2,
            mb: 2,
          }}
        >
          <KpiCard
            title="Employés"
            value={summary.total}
            subtitle="Effectif enregistré"
            icon={
              <PeopleAltOutlinedIcon />
            }
          />

          <KpiCard
            title="Actifs"
            value={summary.active}
            subtitle="Employés en activité"
            icon={
              <PersonIcon />
            }
          />

          <KpiCard
            title="Inactifs"
            value={summary.inactive}
            subtitle="Effectif non actif"
            icon={
              <PersonOffOutlinedIcon />
            }
          />

          <KpiCard
            title="Masse salariale de base"
            value={money(
              summary
                .monthly_base_payroll
            )}
            subtitle="Base mensuelle"
            icon={
              <BadgeOutlinedIcon />
            }
          />
        </Box>


        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                md:
                  "minmax(0, 1fr) 220px 220px",
              },

              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Rechercher"
              placeholder="Nom, matricule, téléphone, poste..."
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
            />

            <TextField
              select
              size="small"
              label="Département"
              value={
                departmentFilter
              }
              onChange={(event) =>
                setDepartmentFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="all">
                Tous
              </MenuItem>

              {departmentOptions.map(
                (department) => (
                  <MenuItem
                    key={
                      department.id
                    }
                    value={
                      department.id
                    }
                  >
                    {department.name}
                  </MenuItem>
                )
              )}
            </TextField>

            <TextField
              select
              size="small"
              label="Statut"
              value={
                statusFilter
              }
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <MenuItem value="all">
                Tous
              </MenuItem>

              {statusOptions.map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }
                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>
          </Box>
        </Paper>


        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom:
                "1px solid",
              borderColor:
                "divider",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={900}
            >
              Liste des employés
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",

              height: {
                xs: 500,
                md: 570,
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}

              disableRowSelectionOnClick

              rowHeight={58}

              pageSizeOptions={[
                5,
                10,
                25,
              ]}

              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}

              sx={{
                border: 0,

                "& .MuiDataGrid-columnHeaders":
                  {
                    bgcolor:
                      "action.hover",
                  },
              }}
            />
          </Box>
        </Paper>
      </Box>


      {/* ===================================================
          CREATE / EDIT EMPLOYEE
      =================================================== */}

      <Dialog
        open={open}
        onClose={
          handleCloseDialog
        }
        fullWidth
        maxWidth="md"

        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 900,
            borderBottom:
              "1px solid",
            borderColor:
              "divider",
          }}
        >
          {editingEmployee
            ? "Modifier l'employé"
            : "Nouvel employé"}
        </DialogTitle>


        <DialogContent
          sx={{
            pt: "24px !important",
          }}
        >
          {!editingEmployee && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              Le matricule sera généré automatiquement après la création.
              Exemple : EMP-00001.
            </Alert>
          )}


          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                sm:
                  "repeat(2, minmax(0, 1fr))",
              },

              gap: 2,
            }}
          >
            <TextField
              required
              label="Prénom"
              value={
                form.first_name
              }
              onChange={
                handleChange(
                  "first_name"
                )
              }
            />

            <TextField
              required
              label="Nom"
              value={
                form.last_name
              }
              onChange={
                handleChange(
                  "last_name"
                )
              }
            />

            <TextField
              label="Téléphone"
              value={form.phone}
              onChange={
                handleChange(
                  "phone"
                )
              }
            />

            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={
                handleChange(
                  "email"
                )
              }
            />

            <TextField
              select
              label="Sexe"
              value={form.gender}
              onChange={
                handleChange(
                  "gender"
                )
              }
            >
              <MenuItem value="">
                Non renseigné
              </MenuItem>

              {genderOptions.map(
                (item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                )
              )}
            </TextField>

            <TextField
              type="date"
              label="Date de naissance"
              value={
                form.birth_date
              }
              onChange={
                handleChange(
                  "birth_date"
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />

            <TextField
              required
              type="date"
              label="Date d'embauche"
              value={
                form.hire_date
              }
              onChange={
                handleChange(
                  "hire_date"
                )
              }
              InputLabelProps={{
                shrink: true,
              }}
            />


            {/* =============================================
                DEPARTEMENT
            ============================================= */}

            <TextField
              required
              select
              label="Département"
              value={
                form.department_id
              }
              onChange={
                handleDepartmentChange
              }
            >
              <MenuItem value="">
                Sélectionner
              </MenuItem>

              {departmentOptions.map(
                (department) => (
                  <MenuItem
                    key={
                      department.id
                    }
                    value={
                      department.id
                    }
                  >
                    {getDepartmentLabel(
                      department
                    )}
                  </MenuItem>
                )
              )}
            </TextField>


            {/* =============================================
                POSTE
            ============================================= */}

            <TextField
              required
              select
              label="Poste"
              value={
                form.job_position_id
              }
              onChange={
                handleChange(
                  "job_position_id"
                )
              }
              disabled={
                !form.department_id
              }
              helperText={
                !form.department_id
                  ? "Sélectionnez d'abord un département."
                  : positionOptions.length ===
                      0
                    ? "Aucun poste actif dans ce département."
                    : ""
              }
            >
              <MenuItem value="">
                Sélectionner
              </MenuItem>

              {positionOptions.map(
                (position) => (
                  <MenuItem
                    key={
                      position.id
                    }
                    value={
                      position.id
                    }
                  >
                    {getPositionLabel(
                      position
                    )}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              select
              label="Type d'emploi"
              value={
                form.employment_type
              }
              onChange={
                handleChange(
                  "employment_type"
                )
              }
            >
              {employmentTypeOptions.map(
                (item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              type="number"
              label="Salaire de base"
              value={
                form.base_salary
              }
              onChange={
                handleChange(
                  "base_salary"
                )
              }
              inputProps={{
                min: 0,
                step: "0.01",
              }}
            />


            <TextField
              select
              label="Devise"
              value={
                form.currency
              }
              onChange={
                handleChange(
                  "currency"
                )
              }
            >
              {currencyOptions.map(
                (currency) => (
                  <MenuItem
                    key={
                      currency.value
                    }
                    value={
                      currency.value
                    }
                  >
                    {currency.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              label="Lieu de travail"
              value={
                form.work_location
              }
              onChange={
                handleChange(
                  "work_location"
                )
              }
            />


            <TextField
              select
              label="Statut"
              value={
                form.status
              }
              onChange={
                handleChange(
                  "status"
                )
              }
            >
              {statusOptions.map(
                (status) => (
                  <MenuItem
                    key={
                      status.value
                    }
                    value={
                      status.value
                    }
                  >
                    {status.label}
                  </MenuItem>
                )
              )}
            </TextField>


            <TextField
              label="Notes / observations"
              multiline
              minRows={3}
              value={form.notes}
              onChange={
                handleChange(
                  "notes"
                )
              }
              sx={{
                gridColumn: {
                  xs: "auto",
                  sm: "1 / -1",
                },
              }}
            />
          </Box>
        </DialogContent>


        <DialogActions
          sx={{
            p: 2,

            borderTop:
              "1px solid",

            borderColor:
              "divider",

            flexDirection: {
              xs: "column-reverse",
              sm: "row",
            },

            gap: 1,

            "& > :not(style)":
              {
                width: {
                  xs: "100%",
                  sm: "auto",
                },
              },
          }}
        >
          <Button
            onClick={
              handleCloseDialog
            }
            disabled={saving}
          >
            Annuler
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={
              saving
                ? (
                  <CircularProgress
                    size={18}
                    color="inherit"
                  />
                )
                : null
            }
          >
            {saving
              ? "Enregistrement..."
              : editingEmployee
                ? "Enregistrer les modifications"
                : "Créer l'employé"}
          </Button>
        </DialogActions>
      </Dialog>


      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        onClose={() =>
          setSnackbar(
            (current) => ({
              ...current,
              open: false,
            })
          )
        }
      >
        <Alert
          variant="filled"
          severity={
            snackbar.severity
          }
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,
                open: false,
              })
            )
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MainLayout>
  )
}